import {NavigationProp} from '@react-navigation/native';
import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {FormErrors, getFormValues, reduxForm, SubmissionError, SubmitHandler} from 'redux-form';
import {confirmContactsChange, requestContactChange} from '../../../../redux/actions/profile';
import {addNotification} from '../../../../redux/actions/snackbar';
import {
  profileChangeContactsNewValueSelector,
  profileChangeContactsResponseSelector,
} from '../../../../redux/selectors/profile';
import {SnackbarNotificationType} from '../../../../shared/components/snackbar/model';
import {LocalParamList} from '../../../../shared/components/tabBar/components/types';
import {defaults} from '../../../../shared/constants/defaults';
import {RegExps} from '../../../../shared/constants/regexps';
import {RequestedOperationType, SendCodeResponsePayload} from '../../../../shared/models/api';
import {Forms} from '../../../../shared/models/forms';
import {FormValidationError, validationPipe} from '../../../../shared/pipes';
import {Screens} from '../../../../shared/routing';
import {DeviceSize, useDeviceSize} from '../../../../shared/theme/helpers';
import {ConfirmPhoneForm} from './model';
import View from './view';

const getFormErrors = (formData: ConfirmPhoneForm) => {
  return validationPipe(formData, [
    {
      names: 'smsCode',
      error: 'Обязательное поле',
      type: 'required',
    },
    {
      names: 'smsCode',
      error: `Код должен состоять из ${defaults.smsCodeLength} цифр`,
      type: 'pattern',
      pattern: RegExps.checkSmsCode,
    },
  ]);
};

const isSmsCodeFilled: (smsCode: string) => boolean = (smsCode) => smsCode?.length === defaults.smsCodeLength;

interface Props {
  handleSubmit: SubmitHandler<
    ConfirmPhoneForm,
    {navigation: NavigationProp<LocalParamList>; onSubmit?: () => void},
    string
  >;
  navigation: NavigationProp<LocalParamList>;
  onSubmit?: () => void;
}

const _ProfileUserPhoneConfirmation: React.FC<Props> = ({handleSubmit, navigation, onSubmit}) => {
  const dispatch = useDispatch();
  const deviceSize = useDeviceSize();
  const newPhone = useSelector(profileChangeContactsNewValueSelector()) as string;
  const sendCodeResponse = useSelector(profileChangeContactsResponseSelector()) as SendCodeResponsePayload;
  const formValues = useSelector(getFormValues(Forms.confirmPhoneForm)) as ConfirmPhoneForm;
  const [isConfirmButtonDisabled, setConfirmButtonDisabled] = React.useState(!isSmsCodeFilled(formValues?.smsCode));

  React.useEffect(() => setConfirmButtonDisabled(!isSmsCodeFilled(formValues?.smsCode)), [formValues]);

  const handleResendCode = () => {
    try {
      dispatch(requestContactChange(newPhone, RequestedOperationType.SetPhoneNumberConfirmation));
    } catch (err) {}
  };

  const handleConfirm = async (formData: ConfirmPhoneForm) => {
    setConfirmButtonDisabled(true);
    const submissionError = getFormErrors(formData);
    if (submissionError) {
      setConfirmButtonDisabled(false);
      throw new SubmissionError(submissionError);
    }
    try {
      await dispatch(confirmContactsChange(formValues.smsCode));
      if (deviceSize !== DeviceSize.desktop) {
        navigation.navigate(Screens.ProfileUserContacts);
      }
      if (onSubmit) {
        onSubmit();
      }
      dispatch(
        addNotification({
          type: SnackbarNotificationType.Success,
          message: 'Новый номер телефона  успешно сохранен.',
        }),
      );
    } catch (e) {
      let errorText = e.data?.[0]?.message || e.message || 'Ошибка. Попробуйте позже';
      throw new SubmissionError({
        smsCode: new FormValidationError(errorText, true),
      } as FormErrors<any, FormValidationError>);
    }
    setConfirmButtonDisabled(false);
  };

  return (
    <View
      onSubmit={(e) => {
        e?.preventDefault();
        handleSubmit(handleConfirm)();
      }}
      onRefresh={handleResendCode}
      isConfirmButtonDisabled={isConfirmButtonDisabled}
      sendCodeResponse={sendCodeResponse}
    />
  );
};

// eslint-disable-next-line no-spaced-func
export const PhoneConfirmation = reduxForm<
  ConfirmPhoneForm,
  {navigation: NavigationProp<LocalParamList>; onSubmit?: () => void}
>({
  form: Forms.confirmPhoneForm,
})(_ProfileUserPhoneConfirmation);
